import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckTenantDeleteBlockersRequestSchema,
  CreateTenantRequestSchema,
  DeleteTenantRequestSchema,
  GetTenantRequestSchema,
  ListTenantsRequestSchema,
  TenantService,
  type TenantStatus,
  UpdateTenantStatusRequestSchema,
} from '../gen/tenants/v1/tenants_pb';
import { createApiTransport, createAuthApiTransport } from '../transport';

function transport(token?: string | null) {
  return token ? createAuthApiTransport(token) : createApiTransport();
}

export interface ListTenantsParams {
  search?: string;
  page?: number;
  pageSize?: number;
  typeFilter?: string;
  statusFilter?: TenantStatus;
}

/**
 * Hook to list tenants with optional server-side search, filter, and pagination.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param parentId - Optional partner ID to filter by
 * @param params - Optional search, filter, and pagination params
 * @returns Query result with list of tenants and totalCount
 */
export function useListTenants(
  token?: string | null,
  parentId?: string,
  params?: ListTenantsParams,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      'tenants',
      'list',
      parentId ?? '',
      !!token,
      params?.search ?? '',
      params?.page ?? 1,
      params?.pageSize ?? 20,
      params?.typeFilter ?? '',
      params?.statusFilter ?? 0,
    ],
    queryFn: async () => {
      const client = createClient(TenantService, transport(token));
      const request = create(ListTenantsRequestSchema, {
        parentId: parentId ?? '',
        search: params?.search ?? '',
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        typeFilter: params?.typeFilter ?? '',
        statusFilter: params?.statusFilter,
      });
      return await client.listTenants(request);
    },
    enabled: !!token && enabled,
  });
}

/**
 * Mutation hook to create a new partner or customer tenant.
 * On success, invalidates the tenants list cache.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for creating a tenant
 */
export function useCreateTenant(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      displayName: string;
      slug: string;
      parentId?: string;
      costCentre?: string;
      organizationId?: string;
    }) => {
      const client = createClient(TenantService, transport(token));
      const request = create(CreateTenantRequestSchema, {
        displayName: input.displayName,
        slug: input.slug,
        parentId: input.parentId ?? '',
        costCentre: input.costCentre ?? '',
        organizationId: input.organizationId ?? '',
      });
      return await client.createTenant(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenants', 'list'] });
      if (variables.parentId) {
        queryClient.invalidateQueries({ queryKey: ['tenants', variables.parentId] });
      }
    },
  });
}

/**
 * Mutation hook to update a tenant's name and slug.
 * On success, invalidates the tenant detail and list caches.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for updating tenant
 */
export function useUpdateTenant(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; displayName: string; slug: string }) => {
      const client = createClient(TenantService, transport(token));
      const { UpdateTenantRequestSchema } = await import('../gen/tenants/v1/tenants_pb');
      const request = create(UpdateTenantRequestSchema, {
        id: input.id,
        displayName: input.displayName,
        slug: input.slug,
      });
      return await client.updateTenant(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenants', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['tenants', 'list'] });
    },
  });
}

/**
 * Mutation hook to activate or deactivate a tenant.
 * On success, invalidates the tenant detail and list caches.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for updating tenant status
 */
export function useUpdateTenantStatus(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; status: TenantStatus }) => {
      const client = createClient(TenantService, transport(token));
      const request = create(UpdateTenantStatusRequestSchema, {
        id: input.id,
        status: input.status,
      });
      return await client.updateTenantStatus(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenants', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['tenants', 'list'] });
    },
  });
}

/**
 * Hook to retrieve a single tenant by ID.
 *
 * @param id - The tenant ID to retrieve
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Query result with tenant data
 */
export function useGetTenant(id: string, token?: string | null) {
  return useQuery({
    queryKey: ['tenants', id, !!token],
    queryFn: async () => {
      const client = createClient(TenantService, transport(token));
      const request = create(GetTenantRequestSchema, { id });
      return await client.getTenant(request);
    },
    enabled: !!id && !!token,
  });
}

/**
 * Mutation hook to delete a tenant.
 * Soft-deletes the tenant by initiating deprovisioning.
 * On success, invalidates the tenant detail and list caches.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for deleting a tenant
 */
export function useDeleteTenant(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const client = createClient(TenantService, transport(token));
      const request = create(DeleteTenantRequestSchema, { id: input.id });
      return await client.deleteTenant(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenants', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['tenants', 'list'] });
    },
  });
}

/**
 * Hook to check which resources block deleting a tenant.
 * Returns child resources that must be removed before the tenant can be deleted.
 *
 * @param tenantId - The tenant ID to check
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param enabled - Whether the query should execute (default: true)
 * @returns Query result with list of blocking resources
 */
export function useCheckTenantDeleteBlockers(
  tenantId: string,
  token?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['tenants', tenantId, 'delete-blockers', !!token],
    queryFn: async () => {
      const client = createClient(TenantService, transport(token));
      const request = create(CheckTenantDeleteBlockersRequestSchema, { tenantId });
      return await client.checkTenantDeleteBlockers(request);
    },
    enabled: !!tenantId && !!token && enabled,
  });
}
